import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  ChangeEvent,
} from "react";
import { AppPage, CheckmarkIcon, CollapsibleBox } from "../../components";
import { Choice, ChoiceList, Button } from "@cmsgov/ds-medicare-gov";
import { CoverageType } from "../QuestionRoutingPage/types";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { AppStore } from "../../app/store";
import { getCwPremiumRanges } from "../../api";
import { CwPremiumRanges, ActionType } from "../../@types";
import { $$ } from "../../helpers/currencyUtilities";
import {
  CONSTANTS,
  sendTealiumEvent,
  useQueryParams,
  useTranslate,
} from "../../helpers";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { withRouter, RouteComponentProps } from "react-router";
import routes from "../../app/routes";
import {
  linkToDrugCoveragePartD,
  linkToGetHelpPayingCosts,
  linkToHowDoMedicareAdvantagePlansWork,
  linkToHowOriginalMedicareWorks,
  linkToJoiningAPlan,
  linkToLowerPrescriptionCosts,
  linkToMedigapInfo,
  linkToWhatMedicareCovers,
} from "../../helpers/CONSTANTS";
import {
  createGa4Event,
  Ga4Event,
  Ga4EventType,
  MctPlanTypeStrings,
} from "../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";

const CoverageWizardInfoPage: FunctionComponent<RouteComponentProps> = ({
  history,
  match,
  location,
}) => {
  const { state, dispatch } = useContext(AppStore.AppContext);
  const flags = useFlags();
  const [coverageTypes, setCoverageTypes] = useState<CoverageType[]>([]);
  const [premiumRanges, setPremiumRanges] = useState<
    CwPremiumRanges | undefined
  >(undefined);
  const [noPlans, setNoPlans] = useState(false);
  const t = useTranslate();
  useQueryParams({ history, match, location });

  useEffect(() => {
    if (state.cwCoverage) {
      sendTealiumEvent(
        `Coverage Wizard - Options: ${state.cwCoverage}`,
        `Toggle Default: ${state.cwCoverage}`
      );

      if (
        [
          CoverageType.PDPAndMedigap,
          CoverageType.PDP,
          CoverageType.MedigapPolicy,
        ].includes(state.cwCoverage)
      ) {
        setCoverageTypes([CoverageType.OriginalMedicare, state.cwCoverage]);
      } else {
        setCoverageTypes([state.cwCoverage]);
      }
    } else {
      setCoverageTypes([CoverageType.OriginalMedicare]);
    }
  }, []);

  useEffect(() => {
    let covType: CoverageType | undefined;

    if (coverageTypes.length === 1) {
      covType = coverageTypes[0];
    } else if (coverageTypes.length > 1) {
      const includesPdp = coverageTypes.includes(CoverageType.PDP);
      const includesMedigap = coverageTypes.includes(
        CoverageType.MedigapPolicy
      );

      if (includesPdp && includesMedigap) {
        covType = CoverageType.PDPAndMedigap;
      } else if (includesPdp) {
        covType = CoverageType.PDP;
      } else if (includesMedigap) {
        covType = CoverageType.MedigapPolicy;
      }
    }

    dispatch({ type: ActionType.UPDATE_CW_COVERAGE, payload: covType });
  }, [coverageTypes]);

  useEffect(() => {
    const { zipcode, fips, year } = state;

    if (zipcode && fips && year) {
      getCwPremiumRanges({ zipcode, fips, year }).then(
        (res): void => {
          setPremiumRanges(res);

          if (res) {
            const notNull = Object.keys(res)
              .filter(k => k !== "request_id")
              .map(k => res[k])
              .filter(v => v !== null);

            setNoPlans(notNull.length === 0);
          }
        },
        (): void => setNoPlans(true)
      );
    }
  }, [state.zipcode, state.fips, state.year]);

  const CoverageIncludes: FunctionComponent<{ coverageType: CoverageType }> = ({
    coverageType,
  }) => {
    const includedInCoverage: {
      [key: string]: {
        partA: boolean;
        partB: boolean;
        drugs: boolean;
        oopHelp: boolean;
        anyDoctor: boolean;
        vision: boolean;
      };
    } = {
      [CoverageType.OriginalMedicare]: {
        partA: true,
        partB: true,
        drugs: false,
        oopHelp: false,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.MedigapPolicy]: {
        partA: true,
        partB: true,
        drugs: false,
        oopHelp: true,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.PDP]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: false,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.PDPAndMedigap]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: true,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.MAPD]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: true,
        anyDoctor: false,
        vision: true,
      },
    };

    const coverage = includedInCoverage[coverageType];

    return (
      <div className="ds-u-border-bottom--1 ds-u-padding-bottom--3">
        <p>{t("cw.info.coverage_includes")}:</p>
        <ul
          className="CoverageWizardInfoPage__coverageIncludesList"
          role="list"
        >
          <li aria-hidden={!coverage.partA} role="listitem">
            <CheckmarkIcon checked={coverage.partA} />
            <span>{t("cw.info.part_a")}</span>
          </li>
          <li aria-hidden={!coverage.partB} role="listitem">
            <CheckmarkIcon checked={coverage.partB} />
            <span>{t("cw.info.part_b")}</span>
          </li>
          <li aria-hidden={!coverage.drugs} role="listitem">
            <CheckmarkIcon checked={coverage.drugs} />{" "}
            <span>{t("cw.info.drugs")}</span>
          </li>
          <li aria-hidden={!coverage.oopHelp} role="listitem">
            <CheckmarkIcon checked={coverage.oopHelp} />
            <span>{t("cw.info.oop_costs")}</span>
          </li>
          <li aria-hidden={!coverage.anyDoctor} role="listitem">
            <CheckmarkIcon checked={coverage.anyDoctor} />
            <span>{t("cw.info.any_doctor")}</span>
          </li>
          <li aria-hidden={!coverage.vision} role="listitem">
            <CheckmarkIcon checked={coverage.vision} />
            <span>{t("cw.info.vision_hearing")}</span>
          </li>
        </ul>
      </div>
    );
  };

  const Explainer: FunctionComponent<{
    label: string | JSX.Element;
    open: boolean;
  }> = ({ open, label, children }) => {
    return (
      <div className="ds-u-fill--gray-lightest ds-u-padding--2 ds-u-margin-bottom--1">
        <CollapsibleBox label={<h4>{label}</h4>} open={open}>
          {children}
        </CollapsibleBox>
      </div>
    );
  };

  const OriginalMedicareExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.om")} open={open}>
      <p className="ds-u-border-bottom--1  ds-u-padding-bottom--2">
        {t("cw.info.om.part_a_premium")}:{" "}
        <span className="ds-u-font-weight--bold">
          {t("cw.info.om.usually_free")}
        </span>
        <br />
        {t("cw.info.om.standard_part_b_premium")}:{" "}
        <span className="ds-u-font-weight--bold">
          {state.year && $$(CONSTANTS.partBPremium[state.year])}
        </span>
      </p>
      <ul className="ds-c-list" role="list">
        <li role="listitem">{t("cw.info.om.covers_80")}</li>
        <li role="listitem">{t("cw.info.om.remaining_20")}</li>
        <li role="listitem">{t("cw.info.om.medigap")}</li>
        <li role="listitem">
          <FormattedHTMLMessage id="cw.info.om.part_d" />
        </li>
        <li role="listitem">{t("cw.info.om.doctor")}</li>
      </ul>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToGetHelpPayingCosts(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "See savings programs that may lower your Medicare costs"
            )
          }
        >
          {t("cw.info.om.see_savings")}
        </a>
      </p>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToHowOriginalMedicareWorks(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Learn how Original Medicare works"
            )
          }
        >
          {t("cw.info.om.learn_how_works")}
        </a>
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p className="ds-u-font-weight--bold">{t("cw.info.om.enroll_late")}</p>
        <p>{t("cw.info.om.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const getTotalMonthlyPremium = (): string => {
    let low = 0;
    let high = 0;

    if (noPlans) {
      return t("cw.info.not_available");
    }

    if (premiumRanges) {
      if (coverageTypes.includes(CoverageType.MedigapPolicy)) {
        const { medigap_premium_min, medigap_premium_max } = premiumRanges;

        low += medigap_premium_min !== null ? medigap_premium_min : 0;
        high += medigap_premium_max !== null ? medigap_premium_max : 0;
      }

      if (coverageTypes.includes(CoverageType.PDP)) {
        const { pdp_premium_min, pdp_premium_max } = premiumRanges;

        low += pdp_premium_min !== null ? pdp_premium_min : 0;
        high += pdp_premium_max !== null ? pdp_premium_max : 0;
      }

      if (coverageTypes.includes(CoverageType.MAPD)) {
        const { ma_premium_min, ma_premium_max } = premiumRanges;

        low += ma_premium_min !== null ? ma_premium_min : 0;
        high += ma_premium_max !== null ? ma_premium_max : 0;
      }

      if (
        (coverageTypes.includes(CoverageType.OriginalMedicare) ||
          coverageTypes.includes(CoverageType.MAPD)) &&
        state.year
      ) {
        low += CONSTANTS.partBPremium[state.year];
        high += CONSTANTS.partBPremium[state.year];
      }
    }

    return low == high ? $$(low) : `${$$(low)}-${$$(high)}`;
  };

  const getPremiumRange = (type: CoverageType): string => {
    if (premiumRanges) {
      if (type === CoverageType.MedigapPolicy) {
        const { medigap_premium_min, medigap_premium_max } = premiumRanges;

        if (medigap_premium_min !== null) {
          let premium = `${$$(medigap_premium_min)}`;

          if (medigap_premium_max !== null) {
            premium =
              medigap_premium_min === medigap_premium_max
                ? `${$$(medigap_premium_min)}`
                : `${$$(medigap_premium_min)}-${$$(medigap_premium_max)}`;
          }

          return premium;
        }
      } else if (type === CoverageType.PDP) {
        const { pdp_premium_min, pdp_premium_max } = premiumRanges;

        if (pdp_premium_min !== null) {
          let premium = `${$$(pdp_premium_min)}`;

          if (pdp_premium_max !== null) {
            premium =
              pdp_premium_min === pdp_premium_max
                ? `${$$(pdp_premium_min)}`
                : `${$$(pdp_premium_min)}-${$$(pdp_premium_max)}`;
          }

          return premium;
        }
      } else if (type === CoverageType.MAPD) {
        const { ma_premium_min, ma_premium_max } = premiumRanges;

        if (ma_premium_min !== null) {
          let premium = `${$$(ma_premium_min)}`;

          if (ma_premium_max !== null) {
            premium =
              ma_premium_min === ma_premium_max
                ? `${$$(ma_premium_min)}`
                : `${$$(ma_premium_min)}-${$$(ma_premium_max)}`;
          }

          return premium;
        }
      }
    }

    return t("cw.info.no_plans_available");
  };

  const MedigapPolicyExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.medigap_policy")} open={open}>
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.medigap_policy.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.MedigapPolicy)}
        </span>
      </p>
      <ul className="ds-c-list" role="list">
        <li role="listitem">{t("cw.info.medigap_policy.pays_20")}</li>
        <li role="listitem">{t("cw.info.medigap_policy.never_drop")}</li>
        <li role="listitem">{t("cw.info.medigap_policy.reflect")}</li>
      </ul>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`#${routes.medigap.plans}?lang=${state.language}`}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Find a Medigap policy in your area"
            )
          }
        >
          {t("cw.info.medigap_policy.find_policy")}
        </a>
      </p>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToMedigapInfo(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Get general information about Medigap"
            )
          }
        >
          {t("cw.info.medigap_policy.general_information")}
        </a>
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p className="ds-u-font-weight--bold">
          {t("cw.info.medigap_policy.enroll_late")}
        </p>
        <p>{t("cw.info.medigap_policy.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const PartDExplainer: FunctionComponent<{ open: boolean }> = ({ open }) => (
    <Explainer
      label={<FormattedHTMLMessage id="cw.info.drug_plan" />}
      open={open}
    >
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.drug_plan.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.PDP)}
        </span>
      </p>
      <ul className="ds-c-list" role="list">
        <li role="listitem">{t("cw.info.drug_plan.separate_drug_coverage")}</li>
        <li role="listitem">{t("cw.info.drug_plan.switch")}</li>
        <li role="listitem">{t("cw.info.drug_plan.vary_cost")}</li>
        <li role="listitem">{t("cw.info.drug_plan.plan_pricing")}</li>
      </ul>
      <p>
        {" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToLowerPrescriptionCosts(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Learn about programs that may lower your drug costs"
            )
          }
        >
          {t("cw.info.drug_plan.reduce_costs")}
        </a>
      </p>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToDrugCoveragePartD(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Get general information on drug plans"
            )
          }
        >
          {t("cw.info.drug_plan.info_drug_plans")}
        </a>
      </p>

      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p className="ds-u-font-weight--bold">
          {t("cw.info.drug_plan.enroll_late")}
        </p>
        <p>{t("cw.info.drug_plan.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const MedicareAdvantageExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.mapd")} open={open}>
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.mapd.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.MAPD)}
        </span>
      </p>
      <ul className="ds-c-list" role="list">
        <li role="listitem">{t("cw.info.mapd.part_a_b")}</li>
        <li role="listitem">{t("cw.info.mapd.doctors")}</li>
        <li role="listitem">{t("cw.info.mapd.drug_coverage")}</li>
        <li role="listitem">{t("cw.info.mapd.extra_benefits")}</li>
        <li role="listitem">{t("cw.info.mapd.oop_costs")}</li>
      </ul>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToHowDoMedicareAdvantagePlansWork(state.language)}
          onClick={() =>
            sendTealiumEvent(
              `Coverage Wizard - Options: ${state.cwCoverage}`,
              "Learn more about Medicare Advantage Plans"
            )
          }
        >
          {t("cw.info.mapd.learn_more")}
        </a>
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p>{t("cw.info.mapd.eligible")}</p>
      </Alert>
    </Explainer>
  );

  const coverageTypeLabel = {
    [CoverageType.MAPD]: t("cw.info.types.mapd"),
    [CoverageType.PDP]: <FormattedHTMLMessage id="cw.info.types.pdp" />,
    [CoverageType.MedigapPolicy]: t("cw.info.types.medigap_policy"),
    [CoverageType.PDPAndMedigap]: (
      <FormattedHTMLMessage id="cw.info.types.pdp_medigap" />
    ),
    [CoverageType.OriginalMedicare]: t("cw.info.types.original_medicare"),
  };

  const getExplainer = (params: {
    open: boolean;
    type: CoverageType;
  }): JSX.Element => {
    const { type, open } = params;

    if (type === CoverageType.MAPD) {
      return <MedicareAdvantageExplainer open={open} />;
    } else if (type === CoverageType.PDP) {
      return <PartDExplainer open={open} />;
    } else if (type === CoverageType.OriginalMedicare) {
      return <OriginalMedicareExplainer open={open} />;
    } else if (type === CoverageType.MedigapPolicy) {
      return <MedigapPolicyExplainer open={open} />;
    }

    return <></>;
  };

  return (
    <AppPage
      heading={t("cw.info.your_options")}
      bannerClassName="CoverageWizardInfoPage__banner"
      outerClassName="ds-u-md-display--flex ds-u-md-flex-direction--column ds-u-md-align-items--center"
      hideLogin={true}
    >
      <div className="CoverageWizardInfoPage__container">
        <p className="ds-u-margin-bottom--3" id="click-next-to">
          {t("cw.info.click_option")}
        </p>
        <ul
          className="CoverageWizardInfoPage__options"
          role="list"
          aria-describedby="click-next-to"
        >
          <li
            role="listitem"
            className="CoverageWizardInfoPage__optionContainer CoverageWizardInfoPage__optionContainer--bordered ds-u-padding-right--4"
          >
            <Choice
              type="radio"
              value="om"
              id="om-coverage-type"
              name="coverage-type"
              checked={coverageTypes.includes(CoverageType.OriginalMedicare)}
              onChange={(): void => {
                if (!coverageTypes.includes(CoverageType.OriginalMedicare)) {
                  sendTealiumEvent(
                    `Coverage Wizard - Options: ${state.cwCoverage}`,
                    `Toggle: ${CoverageType.OriginalMedicare}`
                  );

                  createGa4Event({
                    event_name: Ga4Event.COVERAGE_SELECTED,
                    event_type: Ga4EventType.CONVERSION,
                    mct_plan_type: MctPlanTypeStrings.ORIGINAL_MEDICARE,
                    state,
                    flags,
                  });

                  setCoverageTypes([CoverageType.OriginalMedicare]);
                }
              }}
            >
              <h2 className="ds-text-heading--lg">
                {t("cw.info.types.original_medicare")}
              </h2>
            </Choice>
            <ChoiceList
              choices={[
                {
                  label: t("cw.info.medigap"),
                  value: CoverageType.MedigapPolicy,
                  disabled: coverageTypes.includes(CoverageType.MAPD),
                  checked:
                    coverageTypes.includes(CoverageType.MedigapPolicy) ||
                    coverageTypes.includes(CoverageType.PDPAndMedigap),
                },
                {
                  label: <FormattedHTMLMessage id="cw.info.drug_plan" />,
                  value: CoverageType.PDP,
                  disabled: coverageTypes.includes(CoverageType.MAPD),
                  checked:
                    coverageTypes.includes(CoverageType.PDP) ||
                    coverageTypes.includes(CoverageType.PDPAndMedigap),
                },
              ]}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                const { value, checked, id } = e.target;
                const label = document.querySelector(`[for="${id}"]`);
                const newCovType = value as CoverageType;
                const otherCovType =
                  newCovType === CoverageType.MedigapPolicy
                    ? CoverageType.PDP
                    : CoverageType.MedigapPolicy;
                const checkedVal = checked ? "checked" : "unchecked";

                createGa4Event({
                  event_name: Ga4Event.COVERAGE_SELECTED,
                  event_type: Ga4EventType.CONVERSION,
                  mct_plan_type:
                    newCovType === CoverageType.MAPD
                      ? MctPlanTypeStrings.MEDICARE_ADVANTAGE_PLAN
                      : MctPlanTypeStrings.ORIGINAL_MEDICARE,
                  text: `${
                    label?.textContent ? `${label.textContent}: ` : ""
                  }${checkedVal}`,
                  state,
                  flags,
                });

                sendTealiumEvent(
                  `Coverage Wizard - Options: ${state.cwCoverage}`,
                  `Add Extra Coverage: ${newCovType}: ${checkedVal} | ${otherCovType}: ${
                    coverageTypes.includes(otherCovType)
                      ? "checked"
                      : "unchecked"
                  }`
                );

                if (!coverageTypes.includes(newCovType)) {
                  setCoverageTypes([...coverageTypes, newCovType]);
                } else {
                  setCoverageTypes(
                    coverageTypes.filter(ct => ct !== newCovType)
                  );
                }
              }}
              className="ds-u-margin-top--0 ds-u-margin-left--5"
              labelClassName="ds-text-heading--md"
              label={t("cw.info.extra_coverage")}
              name="checkbox_choices"
              type="checkbox"
            />
          </li>
          <li
            role="listitem"
            className="CoverageWizardInfoPage__optionContainer ds-u-md-padding-left--4"
          >
            <Choice
              type="radio"
              value="mapd"
              name="coverage-type"
              checked={coverageTypes.includes(CoverageType.MAPD)}
              onChange={(): void => {
                if (!coverageTypes.includes(CoverageType.MAPD)) {
                  createGa4Event({
                    event_name: Ga4Event.COVERAGE_SELECTED,
                    event_type: Ga4EventType.CONVERSION,
                    mct_plan_type: MctPlanTypeStrings.MEDICARE_ADVANTAGE_PLAN,
                    state,
                    flags,
                  });

                  sendTealiumEvent(
                    `Coverage Wizard - Options: ${state.cwCoverage}`,
                    `Toggle: ${CoverageType.MAPD}`
                  );

                  setCoverageTypes([CoverageType.MAPD]);
                }
              }}
            >
              <h2 className="ds-text-heading--lg">{t("cw.info.mapd_plans")}</h2>
            </Choice>
          </li>
        </ul>
        <div className="CoverageWizardInfoPage__coverageInfo">
          <h2>{state.cwCoverage && coverageTypeLabel[state.cwCoverage]}</h2>
          <div className="ds-u-border-top--1">
            {noPlans && (
              <Alert variation="error" className="ds-u-margin-top--3">
                {t("cw.info.no_plans")}
              </Alert>
            )}
            <div className="ds-u-md-display--flex ">
              <div className="CoverageWizardInfoPage__coverageBlock CoverageWizardInfoPage__coverageIncludes">
                {state.cwCoverage && (
                  <CoverageIncludes coverageType={state.cwCoverage} />
                )}

                <div className="ds-u-border-bottom--1">
                  {(coverageTypes.includes(CoverageType.MAPD) ||
                    coverageTypes.includes(CoverageType.PDP) ||
                    coverageTypes.includes(CoverageType.MedigapPolicy)) && (
                    <p>
                      {t("cw.info.standard_part_b_premium")}:{" "}
                      {state.year && $$(CONSTANTS.partBPremium[state.year])}
                    </p>
                  )}

                  {coverageTypes.includes(CoverageType.MAPD) && (
                    <p>
                      {t("cw.info.mapd_range")}:{" "}
                      {getPremiumRange(CoverageType.MAPD)}
                    </p>
                  )}

                  {coverageTypes.includes(CoverageType.MedigapPolicy) && (
                    <p>
                      {t("cw.info.medigap_range")}:{" "}
                      {getPremiumRange(CoverageType.MedigapPolicy)}
                    </p>
                  )}

                  {coverageTypes.includes(CoverageType.PDP) && (
                    <p>
                      {t("cw.info.drug_range")}:{" "}
                      {getPremiumRange(CoverageType.PDP)}
                    </p>
                  )}
                </div>

                <p id="prem" className="ds-u-margin-bottom--0">
                  {t("cw.info.total_monthly")}
                </p>

                <h3
                  id="cost"
                  aria-labelledby="prem cost"
                  className="ds-u-margin--0"
                >
                  {getTotalMonthlyPremium()}
                </h3>
              </div>
              <div className="CoverageWizardInfoPage__coverageBlock CoverageWizardInfoPage__coverageExplained">
                {[...coverageTypes]
                  .reverse()
                  .map((type, i) => getExplainer({ open: i === 0, type }))}
              </div>
            </div>
          </div>
        </div>
        <div>
          <h3>{t("cw.info.ready")}</h3>
          <p>
            <FormattedMessage
              id="cw.info.learn_more"
              values={{
                learnMoreLink: (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={linkToJoiningAPlan(state.language)}
                    onClick={() =>
                      sendTealiumEvent(
                        `Coverage Wizard - Options: ${state.cwCoverage}`,
                        "Learn more about when you can join a plan"
                      )
                    }
                  >
                    {t("cw.info.join")}
                  </a>
                ),
                seeWhatMedicareCovers: (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={linkToWhatMedicareCovers(state.language)}
                    onClick={() =>
                      sendTealiumEvent(
                        `Coverage Wizard - Options: ${state.cwCoverage}`,
                        "See what Medicare covers"
                      )
                    }
                  >
                    {t("cw.info.see_what_medicare_covers")}
                  </a>
                ),
              }}
            />
          </p>
          <p className="ds-u-margin-bottom--3">
            {state.cwCoverage && (
              <FormattedMessage
                id="cw.info.you_chose"
                values={{
                  planType: (
                    <span className="ds-u-font-weight--bold">
                      {coverageTypeLabel[state.cwCoverage]}
                    </span>
                  ),
                }}
              />
            )}
            <Button
              variation="transparent"
              className="CoverageWizardInfoPage__changeSelection"
              onClick={(): void => {
                sendTealiumEvent(
                  `Coverage Wizard - Options: ${state.cwCoverage}`,
                  "Change Selection"
                );

                document.getElementById("om-coverage-type")?.focus();
              }}
            >
              {t("cw.info.change_selection")}
            </Button>
          </p>

          <Button
            variation="primary"
            onClick={(): void => {
              sendTealiumEvent(
                `Coverage Wizard - Options: ${state.cwCoverage}`,
                "Look at Plans"
              );

              createGa4Event({
                event_name: Ga4Event.CW_COMPLETED,
                state,
                flags,
              });

              history.push(
                `${routes.questionRouting}?zip=${state.zipcode}&fips=${state.fips}`
              );
            }}
          >
            {t("cw.info.look_plans")}
          </Button>
        </div>
      </div>
    </AppPage>
  );
};

export default withRouter(CoverageWizardInfoPage);
